import { create } from 'zustand';
import type {
  AppNotification,
  NotificationCategory,
  NotificationPushEvent,
} from '../types/notifications';

interface NotificationState {
  /** All persisted notifications (Bell inbox + Action inbox combined) */
  notifications: AppNotification[];
  /** Whether the initial hydration fetch has run */
  hydrated: boolean;

  // ── Mutations ───────────────────────────────────────────────────────────────
  setNotifications: (notifications: AppNotification[]) => void;
  addNotification: (event: NotificationPushEvent, recipient: string) => void;
  markRead: (id: string) => void;
  markActioned: (id: string) => void;
  removeOne: (id: string) => void;
  clearCategory: (category: NotificationCategory) => void;
  setHydrated: () => void;
}

export const useNotificationStore = create<NotificationState>((set) => ({
  notifications: [],
  hydrated: false,

  setNotifications: (notifications) => set({ notifications }),

  addNotification: (event, recipient) => {
    const next: AppNotification = {
      id: event.id,
      recipient,
      category: event.category,
      eventType: event.eventType,
      workItemId: event.workItemId,
      workItemTitle: event.workItemTitle,
      route: event.route,
      detail: event.detail,
      adoUrl: event.adoUrl,
      cfUrl: event.cfUrl,
      read: false,
      actioned: false,
      createdAt: event.createdAt,
    };
    set((s) => ({
      notifications: [next, ...s.notifications].slice(0, 100),
    }));
  },

  markRead: (id) =>
    set((s) => ({
      notifications: s.notifications.map((n) => (n.id === id ? { ...n, read: true } : n)),
    })),

  markActioned: (id) =>
    set((s) => ({
      notifications: s.notifications.map((n) =>
        n.id === id ? { ...n, actioned: true, read: true } : n,
      ),
    })),

  removeOne: (id) =>
    set((s) => ({
      notifications: s.notifications.filter((n) => n.id !== id),
    })),

  clearCategory: (category) =>
    set((s) => ({
      notifications: s.notifications.filter((n) => n.category !== category),
    })),

  setHydrated: () => set({ hydrated: true }),
}));

// ── Stable selectors (return primitives — safe for useSyncExternalStore) ─────

export const selectUnreadCount = (s: NotificationState): number =>
  s.notifications.filter((n) => n.category === 'notification' && !n.read).length;

export const selectUnactionedCount = (s: NotificationState): number =>
  s.notifications.filter((n) => n.category === 'action' && !n.actioned).length;
